'use client';

/**
 * GlassCard Component
 *
 * A glassmorphism card with subtle glow effects on hover.
 * Automatically adapts to light/dark theme.
 */

import Box, { type BoxProps } from '@mui/material/Box';
import { styled, useTheme } from '@mui/material/styles';
import { type HTMLMotionProps, motion } from 'framer-motion';
import type React from 'react';
import { forwardRef } from 'react';
import { liquidGlass, liquidRadius } from '../../theme/liquidGlass';
import { type AccentColor, accentColors, getNeonStyles, type NeonAccent } from '../../theme/palette';
import { createNeonGlow, glassEffects, glowEffects } from '../../theme/shadows';
import { interactionAnimations, motionPresets, transitionPresets } from '../../theme/transitions';

// ============================================================================
// Types
// ============================================================================

export interface GlassCardProps extends BoxProps {
  /**
   * Polymorphic root element type (e.g., 'a', 'div', 'button').
   * When set to 'a', also pass `href` for link cards.
   */
  component?: React.ElementType;

  /**
   * Link destination (when `component="a"`).
   */
  href?: string;

  /**
   * Enable hover glow effect
   * @default false
   */
  glowOnHover?: boolean;

  /**
   * Glow color variant
   * @default 'mixed'
   */
  glowColor?: 'primary' | 'secondary' | 'mixed';

  /**
   * Enable hover lift animation
   * @default false
   */
  hoverLift?: boolean;

  /**
   * Enable entrance animation
   * @default false
   */
  animate?: boolean;

  /**
   * Animation delay in seconds
   * @default 0
   */
  animationDelay?: number;

  /**
   * Card variant
   * @default 'elevated'
   */
  variant?: 'elevated' | 'outlined' | 'flat';

  /**
   * Enable blur effect (glassmorphism)
   * @default true
   */
  blur?: boolean;

  /**
   * Custom border radius
   */
  borderRadius?: number | string;

  /**
   * Accent border position
   * @default undefined (no accent border)
   */
  accentBorder?: 'left' | 'top' | 'right' | 'bottom';

  /**
   * Accent border color
   * @default 'teal'
   */
  accentColor?: AccentColor;

  /**
   * Accent border width in pixels
   * @default 3
   */
  accentWidth?: number;

  /**
   * Enable neon glow mode with a specific accent color.
   * When set, overrides the default glass styling with neon glow aesthetic.
   * @default undefined (uses standard glass styling)
   */
  neonAccent?: NeonAccent;

  /**
   * Enable rotating animated gradient border (conic-gradient).
   * @default false
   */
  animatedBorder?: boolean;

  /**
   * Custom gradient for the animated border.
   * @default chroma gradient
   */
  borderGradient?: string;

  /**
   * Enable ambient breathing glow shadow.
   * @default false
   */
  pulseGlow?: boolean;

  /**
   * Click handler
   */
  onClick?: () => void;

  /**
   * Children
   */
  children?: React.ReactNode;

  /**
   * Optional media image displayed at the top of the card.
   */
  mediaSrc?: string;

  /**
   * Media alt text for accessibility.
   */
  mediaAlt?: string;

  /**
   * Media height in pixels.
   * @default 180
   */
  mediaHeight?: number;
}

// ============================================================================
// Styled Components
// ============================================================================

interface StyledCardProps {
  $isDark: boolean;
  $variant: 'elevated' | 'outlined' | 'flat';
  $blur: boolean;
  $glowOnHover: boolean;
  $glowColor: 'primary' | 'secondary' | 'mixed';
  $hoverLift: boolean;
  $borderRadius: number | string;
  $clickable: boolean;
  $accentBorder?: 'left' | 'top' | 'right' | 'bottom';
  $accentColor?: string;
  $accentWidth: number;
  $neonAccent?: NeonAccent;
  $animatedBorder: boolean;
  $borderGradient?: string;
  $pulseGlow: boolean;
}

// Helper to get glow value safely
const getGlowValue = (color: 'primary' | 'secondary' | 'mixed', intensity: 'subtle' | 'medium'): string =>
  glowEffects[color][intensity];

const StyledCard = styled(motion.div, {
  shouldForwardProp: (prop) => !String(prop).startsWith('$'),
})<StyledCardProps>(
  ({
    theme,
    $isDark,
    $variant,
    $blur,
    $glowOnHover,
    $glowColor,
    $hoverLift: _hoverLift,
    $borderRadius,
    $clickable,
    $accentBorder,
    $accentColor,
    $accentWidth,
    $neonAccent,
    $animatedBorder,
    $borderGradient,
    $pulseGlow,
  }) => {
    const glass = $isDark ? glassEffects.dark : glassEffects.light;
    const lgMaterial = $isDark ? liquidGlass.regular.dark : liquidGlass.regular.light;

    // Get neon styles if neonAccent is set
    const neonStyles = $neonAccent ? getNeonStyles($neonAccent, $isDark) : null;

    // Get accent border styles
    const getAccentBorderStyles = () => {
      if (!$accentBorder || !$accentColor) return {};

      const borderMap = {
        left: { borderLeft: `${$accentWidth}px solid ${$accentColor}` },
        top: { borderTop: `${$accentWidth}px solid ${$accentColor}` },
        right: { borderRight: `${$accentWidth}px solid ${$accentColor}` },
        bottom: { borderBottom: `${$accentWidth}px solid ${$accentColor}` },
      };

      return borderMap[$accentBorder];
    };

    // Specular highlight pseudo-element styles
    const specularOverlay = {
      '&::after': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: '50%',
        borderRadius: 'inherit',
        background: $isDark
          ? 'linear-gradient(180deg, rgba(255,255,255,0.03) 0%, transparent 100%)'
          : 'linear-gradient(180deg, rgba(255,255,255,0.18) 0%, transparent 100%)',
        pointerEvents: 'none',
        zIndex: 0,
      },
    };

    // If neonAccent is set, use liquid glass + accent tint styling
    if (neonStyles && $neonAccent) {
      return {
        position: 'relative',
        borderRadius: typeof $borderRadius === 'number' ? `${$borderRadius}px` : $borderRadius,
        padding: theme.spacing(2),
        overflow: 'hidden',
        transition: transitionPresets.card,
        cursor: $clickable ? 'pointer' : 'default',

        // Responsive padding for mobile
        [theme.breakpoints.down('sm')]: {
          padding: theme.spacing(1.5),
        },

        // Liquid glass + neon tint
        background: neonStyles.background,
        backdropFilter: lgMaterial.blur,
        WebkitBackdropFilter: lgMaterial.blur,
        border: `0.5px solid ${neonStyles.border}`,
        boxShadow: $glowOnHover
          ? `${lgMaterial.specular}, ${lgMaterial.innerShadow}`
          : `${lgMaterial.specular}, ${lgMaterial.innerShadow}, ${createNeonGlow($neonAccent, 'subtle')}`,

        ...specularOverlay,

        // Hover effects
        '&:hover': {
          boxShadow: $glowOnHover
            ? `${lgMaterial.specular}, ${lgMaterial.innerShadow}, ${createNeonGlow($neonAccent, 'medium')}`
            : undefined,
        },

        // Active state
        '&:active': {
          ...($clickable && {
            transform: 'scale(0.99)',
          }),
        },
      };
    }

    // Default Liquid Glass styling
    return {
      position: 'relative',
      borderRadius: typeof $borderRadius === 'number' ? `${$borderRadius}px` : $borderRadius,
      padding: theme.spacing(2),
      overflow: 'hidden',
      transition: transitionPresets.card,
      cursor: $clickable ? 'pointer' : 'default',

      // Responsive padding for mobile
      [theme.breakpoints.down('sm')]: {
        padding: theme.spacing(1.5),
      },

      // Variant styles — Liquid Glass material
      ...($variant === 'elevated' && {
        background: glass.background,
        backdropFilter: $blur ? glass.backdropBlur : 'none',
        WebkitBackdropFilter: $blur ? glass.backdropBlur : 'none',
        border: glass.border.startsWith('0.5px') ? glass.border : `0.5px solid ${glass.border}`,
        boxShadow: `${lgMaterial.specular}, ${lgMaterial.innerShadow}, ${glass.shadow.split(', inset')[0]}`,
        ...specularOverlay,
      }),

      ...($variant === 'outlined' && {
        background: 'transparent',
        border: `0.5px solid ${glass.border}`,
        boxShadow: lgMaterial.specular,
        ...specularOverlay,
      }),

      ...($variant === 'flat' && {
        background: $isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
        border: 'none',
        boxShadow: 'none',
      }),

      // Apply accent border (overrides the side border)
      ...getAccentBorderStyles(),

      // Hover effects — subtle scale instead of translateY
      '&:hover': {
        ...($variant === 'elevated' && {
          background: glass.backgroundHover,
          boxShadow: $glowOnHover
            ? `${lgMaterial.specular}, ${lgMaterial.innerShadow}, ${glass.shadowHover.split(', inset')[0]}, ${getGlowValue($glowColor, 'medium')}`
            : `${lgMaterial.specular}, ${lgMaterial.innerShadow}, ${glass.shadowHover.split(', inset')[0]}`,
          // Re-apply accent border on hover
          ...getAccentBorderStyles(),
        }),

        ...($variant === 'outlined' && {
          background: $isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
          boxShadow: $glowOnHover
            ? `${lgMaterial.specular}, ${getGlowValue($glowColor, 'medium')}`
            : lgMaterial.specular,
          // Re-apply accent border on hover
          ...getAccentBorderStyles(),
        }),

        ...($variant === 'flat' && {
          background: $isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
          boxShadow: $glowOnHover ? getGlowValue($glowColor, 'subtle') : 'none',
          // Re-apply accent border on hover
          ...getAccentBorderStyles(),
        }),
      },

      // Active state — press-in feel
      '&:active': {
        ...($clickable && {
          transform: 'scale(0.99)',
        }),
      },

      // Animated gradient border (conic-gradient rotation trick)
      ...($animatedBorder && {
        position: 'relative' as const,
        overflow: 'visible',
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: '-2px',
          borderRadius: 'inherit',
          padding: '2px',
          background:
            $borderGradient || 'conic-gradient(from var(--border-angle, 0deg), #635BFF, #00D4FF, #A855F7, #635BFF)',
          WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
          WebkitMaskComposite: 'xor',
          maskComposite: 'exclude',
          animation: 'spinBorder 4s linear infinite',
          zIndex: -1,
        },
        '@keyframes spinBorder': {
          from: { '--border-angle': '0deg' },
          to: { '--border-angle': '360deg' },
        },
        '@property --border-angle': {
          syntax: '"<angle>"',
          inherits: 'false',
          initialValue: '0deg',
        },
      }),

      // Breathing glow pulse — softened for Liquid Glass
      ...($pulseGlow && {
        animation: 'glassBreathing 3s ease-in-out infinite',
        '@keyframes glassBreathing': {
          '0%, 100%': {
            boxShadow: `${lgMaterial.specular}, 0 0 8px rgba(99,91,255,0.08), 0 0 16px rgba(0,212,255,0.04)`,
          },
          '50%': {
            boxShadow: `${lgMaterial.specular}, 0 0 16px rgba(99,91,255,0.16), 0 0 32px rgba(0,212,255,0.08)`,
          },
        },
      }),
    };
  },
);

// ============================================================================
// Component
// ============================================================================

export const GlassCard = forwardRef<HTMLDivElement, GlassCardProps>(
  (
    {
      children,
      glowOnHover = false,
      glowColor = 'mixed',
      hoverLift = false,
      animate = false,
      animationDelay = 0,
      variant = 'elevated',
      blur = true,
      borderRadius = liquidRadius.md,
      accentBorder,
      accentColor = 'teal',
      accentWidth = 3,
      neonAccent,
      animatedBorder = false,
      borderGradient,
      pulseGlow = false,
      mediaSrc,
      mediaAlt,
      mediaHeight = 180,
      onClick,
      sx,
      ...props
    },
    ref,
  ) => {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';

    // Animation variants
    const animationProps: HTMLMotionProps<'div'> = animate
      ? {
          initial: motionPresets.fadeInUp.initial,
          animate: motionPresets.fadeInUp.animate,
          transition: {
            ...motionPresets.fadeInUp.transition,
            delay: animationDelay,
          },
        }
      : {};

    // Hover animation (when not using CSS hover)
    const hoverProps = hoverLift
      ? {
          whileHover: interactionAnimations.cardHover,
        }
      : {};

    // Get the actual accent color value
    const resolvedAccentColor = accentBorder ? accentColors[accentColor] : undefined;

    return (
      <StyledCard
        ref={ref}
        $isDark={isDark}
        $variant={variant}
        $blur={blur}
        $glowOnHover={glowOnHover}
        $glowColor={glowColor}
        $hoverLift={false} // We use framer-motion for hover instead
        $borderRadius={borderRadius}
        $clickable={!!onClick}
        $accentBorder={accentBorder}
        $accentColor={resolvedAccentColor}
        $accentWidth={accentWidth}
        $neonAccent={neonAccent}
        $animatedBorder={animatedBorder}
        $borderGradient={borderGradient}
        $pulseGlow={pulseGlow}
        onClick={onClick}
        sx={sx}
        {...animationProps}
        {...hoverProps}
        {...(props as Record<string, unknown>)}
      >
        {mediaSrc && (
          <Box
            component="img"
            src={mediaSrc}
            alt={mediaAlt ?? ''}
            loading="lazy"
            decoding="async"
            sx={(theme) => {
              const topRadius = typeof borderRadius === 'number' ? `${borderRadius}px` : borderRadius;

              if (animatedBorder) {
                return {
                  width: '100%',
                  height: mediaHeight,
                  objectFit: 'cover',
                  display: 'block',
                  marginTop: 0,
                  marginLeft: 0,
                  marginRight: 0,
                  marginBottom: theme.spacing(2),
                  borderTopLeftRadius: topRadius,
                  borderTopRightRadius: topRadius,
                };
              }

              return {
                width: `calc(100% + ${theme.spacing(4)})`,
                height: mediaHeight,
                objectFit: 'cover',
                display: 'block',
                marginTop: `-${theme.spacing(2)}`,
                marginLeft: `-${theme.spacing(2)}`,
                marginRight: `-${theme.spacing(2)}`,
                marginBottom: theme.spacing(2),
                borderTopLeftRadius: topRadius,
                borderTopRightRadius: topRadius,
                [theme.breakpoints.down('sm')]: {
                  width: `calc(100% + ${theme.spacing(3)})`,
                  marginTop: `-${theme.spacing(1.5)}`,
                  marginLeft: `-${theme.spacing(1.5)}`,
                  marginRight: `-${theme.spacing(1.5)}`,
                },
              };
            }}
          />
        )}
        {children}
      </StyledCard>
    );
  },
);

GlassCard.displayName = 'GlassCard';

export default GlassCard;
